/* eslint-disable */
import React, { useState, useRef, useEffect, useContext } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PopupMenu } from 'quantux-ui';
import CaseScheduleContext from '../case-schedule/case-schedule-context';
import WarningDialog from '../warning-dialog';
import { titleCase, calculateAge, parseDateFromIsoString, debugLogToConsole, makeIsoDateString, padZero } from '../../utils';
import { blue, gray, salmon } from '../../theme';

const idIcon = (
  <svg
    width="20"
    height="15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.406 0h17.188C19.37 0 20 .629 20 1.406v12.188C20 14.37 19.371 15 18.594 15H1.406A1.405 1.405 0 0 1 0 13.594V1.406C0 .63.629 0 1.406 0ZM8.75 11.094c0 .777-.629 1.406-1.406 1.406H3.906A1.405 1.405 0 0 1 2.5 11.094V9.375c0-1.078.545-2.027 1.375-2.59a2.5 2.5 0 1 1 3.5 0 3.122 3.122 0 0 1 1.375 2.59v1.719ZM6.719 5a1.094 1.094 0 1 0-2.19.001A1.094 1.094 0 0 0 6.72 5ZM3.906 9.473c0-.006 0-.012.002-.02-.002-.025-.002-.053-.002-.078a1.719 1.719 0 0 1 3.438 0c0 .027 0 .053-.002.078.002.006.002.012.002.02v1.523a.097.097 0 0 1-.098.098H4.004a.097.097 0 0 1-.098-.098V9.473Zm14.688 4.12H1.406V1.407h17.188v12.188Zm-3.047-5.39h-4.844a.703.703 0 1 1 0-1.406h4.844a.703.703 0 1 1 0 1.406Zm-4.844 3.203h3.594a.703.703 0 1 0 0-1.406h-3.594a.703.703 0 1 0 0 1.406ZM16.797 5h-6.094a.703.703 0 1 1 0-1.406h6.094a.703.703 0 1 1 0 1.406Z"
      fill="#9CF"
    />
  </svg>
);

const TODAY = new Date();

const PatientInfo = (props) => {
  const [avatar, setAvatar] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userName, setUserName] = useState('');
  const [ssn1, setSsn1] = useState('');
  const [ssn2, setSsn2] = useState('');
  const [ssn3, setSsn3] = useState('');
  const [dob, setDob] = useState('');
  const [dobYear, setDobYear] = useState(TODAY.getFullYear());
  const [dobMonth, setDobMonth] = useState(TODAY.getMonth());
  const [dobDate, setDobDate] = useState(TODAY.getDate());
  const [patientAge, setPatientAge] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('N/A');
  const [homeAddress, setHomeAddress] = useState({});
  const [mailingAddressSameAsHomeAddress, setMailingAddressSameAsHomeAddress] = useState(false);
  const [mailingAddress, setMailingAddress] = useState({});
  const [mobile, setMobile] = useState('');
  const [contactType, setContactType] = useState('mobile');
  const [patientIdFrontScan, setPatientIdFrontScan] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [patientIdBackScan, setPatientIdBackScan] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showPopupMenu, setShowPopupMenu] = useState(false);
  const [showPatientDeleteWarning, setShowPatientDeleteWarning] = useState(false);
  // -- Refs ------------------------------------------------------------
  const popupMenuRef = useRef();
  // -- CaseSchedule Context --------------------------------------------
  let readOnlyMode = false;
  const context = useContext(CaseScheduleContext);
  if (context) {
    readOnlyMode = context.readOnlyMode;
  }

  useEffect(() => {
    const {
      avatar,
      firstName = '',
      middleName = '',
      lastName = '',
      userName,
      ssn1 = '',
      ssn2 = '',
      ssn3 = '',
      dob = new Date().toISOString(),
      email,
      gender: patientGender = '',
      homeAddress = {},
      mailingAddress = {},
      mailingAddressSameAsHomeAddress,
      mobile,
      contactType = 'mobile',
      patientIdFrontScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg',
      patientIdBackScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'
    } = props.patientData;

    setAvatar(avatar);
    setFirstName(firstName);
    setMiddleName(middleName);
    setLastName(lastName);
    setUserName(userName);
    setSsn1(ssn1);
    setSsn2(ssn2);
    setSsn3(ssn3);
    setDob(dob);
    setEmail(email);
    if (patientGender === 'Male') {
      setGender('M');
    } else if (patientGender === 'Female') {
      setGender('F');
    }
    setHomeAddress(homeAddress);
    setMailingAddress(mailingAddress);
    setMailingAddressSameAsHomeAddress(mailingAddressSameAsHomeAddress);
    setMobile(mobile);
    setContactType(contactType);
    setPatientIdFrontScan(patientIdFrontScan);
    setPatientIdBackScan(patientIdBackScan);
  }, [props.patientData]);

  useEffect(() => {
    if (dob.length > 0) {
      const {year, month, date} = parseDateFromIsoString(dob);
      setDobYear(year);
      setDobMonth(month);
      setDobDate(date);
      debugLogToConsole(`age = ${calculateAge(`${padZero(+month + 1)}/${padZero(+date)}/${year}`)}`);
      // "08/13/1974"
      setPatientAge(calculateAge(`${padZero(+month + 1)}/${padZero(+date)}/${year}`));
    }
  }, [dob]);

  let phoneUrl = '';
  if (mobile && mobile !== '') {
    const tmp = mobile.replace(/-/g, '');
    phoneUrl = `tel:+1${tmp}`;
  }

  const homeAddressStreet = homeAddress.streetAddress1 || '';
  const homeAddressSuite = homeAddress.suite || '';
  const homeAddressCity = homeAddress.city || '';
  const homeAddressState = homeAddress.state || '';
  const homeAddressZip = homeAddress.zip || '';

  let homeAddressUrl = '';
  if (homeAddressStreet !== '' && homeAddressCity !== '' && homeAddressState !== '' && homeAddressZip !== '') {
    const tmpAddress = `${homeAddressStreet} ${homeAddressCity} ${homeAddressState} ${homeAddressZip}`;
    homeAddressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(tmpAddress);
  }

  let mailingAddressStreet = '';
  let mailingAddressSuite = '';
  let mailingAddressCity = '';
  let mailingAddressState = '';
  let mailingAddressZip = '';
  if (!mailingAddressSameAsHomeAddress) {
    mailingAddressStreet = mailingAddress.streetAddress1 || '';
    mailingAddressSuite = mailingAddress.suite || '';
    mailingAddressCity = mailingAddress.city || '';
    mailingAddressState = mailingAddress.state || '';
    mailingAddressZip = mailingAddress.zip || '';
  }

  let mailingAddressUrl = '';
  if (mailingAddressStreet !== '' && mailingAddressCity !== '' && mailingAddressState !== '' && mailingAddressZip !== '') {
    const tmpAddress = `${mailingAddressStreet} ${mailingAddressCity} ${mailingAddressState} ${mailingAddressZip}`;
    mailingAddressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(tmpAddress);
  }

  const styles = StyleSheet.create({
    dataValue: {
      color: blue[200]
    },
    patientCard: {
      background: blue[700],
      color: blue[200],
      padding: '8px',
      borderRadius: '4px'
    },
    cardEllipsisContainer: {
      position: 'relative'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      background: 'black',
      padding: '1px',
      zIndex: '100',
      border: '1px solid #084080'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '100%',
      marginTop: '1px',
      paddingLeft: '8px',
      color: '#00A0FF',
      fontSize: '14px',
      cursor: 'pointer',
      background: '#002448',
      ':hover': {
        background: '#0066CC'
      }
    },
    dangerGridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '100%',
      marginTop: '1px',
      paddingLeft: '8px',
      color: '#FF6060',
      fill: '#FF6060',
      fontSize: '14px',
      background: '#002448',
      ':hover': {
        background: '#0066CC'
      }
    }
  });

  return (
    <div
      className={props.className}
      style={{ overflowY: 'auto' }}
    >
      {/* Patient Delete Warning */}
      {showPatientDeleteWarning &&
        <WarningDialog
          show={showPatientDeleteWarning}
          title='Delete Patient'
          message='Do you want to delete patient data?'
          onCancel={() => setShowPatientDeleteWarning(false)}
          onContinue={() => {
            setShowPatientDeleteWarning(false);
            props.onDeletePatient(props.patientData._id);
          }}
        />
      }

      {/* Patient Card */}
      <div className={css(styles.patientCard)}>
        <div className='fx'>
          {/* Patient Avatar */}
          <div>
            {!avatar || avatar.length === 0
              ? <svg
                width="46"
                height="46"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3 0a3 3 0 0 0-3 3v40a3 3 0 0 0 3 3h40a3 3 0 0 0 3-3V3a3 3 0 0 0-3-3H3Zm29.344 13.656a8.614 8.614 0 0 1-8.625 8.625 8.613 8.613 0 0 1-8.625-8.625 8.613 8.613 0 0 1 8.625-8.625 8.614 8.614 0 0 1 8.625 8.625ZM6.469 36.657c0-5.75 11.5-8.912 17.25-8.912s17.25 3.162 17.25 8.912v2.875h-34.5v-2.875Z"
                  fill="#96C8FF"
                />
              </svg>
              : <div>
                <img style={{ maxHeight: '46px' }} src={avatar} />
              </div>
            }
          </div>
          {/* Patient full name, ssn, dob and gender */}
          <div
            className='ml-2'
            style={{
              lineHeight: '14px',
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              flexGrow: '1'
            }}
          >
            <div style={{ flexGrow: '1' }}>
              {/* Full Name */}
              <div className='fx'>
                <div className='ml-2'>
                  {`${titleCase(firstName)}${middleName.length > 0 ? ` ${titleCase(middleName)}` : ''} ${titleCase(lastName)}`}
                </div>
                {!props.noEdit && <i className='ml-2 fa fa-pencil' onClick={props.onEdit} />}
              </div>
              {/* Username */}
              <div
                className='ml-2'
                style={{
                  color: blue[500]
                }}
              >
                {`@${userName}`}
              </div>
              {/* SSN */}
              <div className='fx fx__v-center mt-2'>
                <div className='ml-2'>SSN</div>
                <div className='ml-2'>
                  <span style={{ color: (ssn1 === '' || ssn1.length < 3) ? salmon[500] : '' }}>
                    {`(${(!isNaN(ssn1) && ssn1.length === 3) ? 'XXX' : '???'}`}
                  </span>-
                  <span style={{ color: (ssn2 === '' || ssn2.length < 2) ? salmon[500] : '' }}>
                    {`${(!isNaN(ssn2) && ssn2.length === 2) ? 'XX' : '??'}`}
                  </span>-
                  <span style={{ color: (ssn3 === '' || ssn3.length < 4) ? salmon[500] : '' }}>
                    {`${(!isNaN(ssn3) && ssn3.length === 4) ? ssn3 : '????'})`}
                  </span>
                  {/* {`${(!isNaN(ssn1) && ssn1.length === 3) ? 'XXX' : '???'}-${(!isNaN(ssn2) && ssn2.length === 2) ? 'XX' : '??'}-${(!isNaN(ssn3) && ssn3.length === 4) ? ssn3 : '????'}`} */}
                </div>
              </div>
              {/* DOB, Age & Gender */}
              <div className='fx fx__v-center mt-2'>
                <div className='ml-2'>
                  {`${dobMonth + 1}/${dobDate}/${dobYear}, ${patientAge}, ${gender}`}
                </div>
              </div>
            </div>
            {/* Ellipsis */}
            <div
              className={css(styles.cardEllipsisContainer)}
              ref={popupMenuRef}
            >
              <i
                className='fa fa-ellipsis-h'
                onClick={() => {
                  if (!readOnlyMode) {
                    setShowPopupMenu(true);
                  }
                }}
              />
              <PopupMenu
                top={14}
                right={4}
                position="absolute"
                visible={showPopupMenu}
                parent={popupMenuRef}
                onOutsideClick={() => { setShowPopupMenu(false); }}
              >
                <div className={css(styles.gridEllipsisMenuContainer)}>
                  {/* Edit Profiel */}
                  {!props.noEdit &&
                    <div
                      className={css(styles.gridEllipsisMenuItem)}
                      onClick={props.onEdit}
                    >
                      <span className='ml-2'>
                        Edit Profile
                      </span>
                    </div>
                  }
                  {/* Delete Patient */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => setShowPatientDeleteWarning(true)}
                  >
                    <span className='ml-2'>
                      Delete Patient
                    </span>
                  </div>
                </div>
              </PopupMenu>
            </div>
          </div>
        </div>
        {/* Contact # */}
        <div className='fx fx__v-center mt-2'>
          <div style={{ width: '20px' }}>
            <i className={`mr-2 fa ${contactType.toLowerCase() === 'mobile' ? 'fa-2x fa-mobile' : 'fa-phone'}`} />
          </div>
          {/* <div>Contact:</div> */}
          <div className={`ml-2 ${css(styles.dataValue)}`}>
            {mobile && mobile !== ''
              ? <a href={phoneUrl}>{mobile}</a>
              : <div style={{ color: salmon[500] }}>Required</div>
            }
          </div>
        </div>
        {/* Email */}
        <div className='fx fx__v-center mt-2'>
          <div style={{ width: '20px' }}>
            <i className='mr-2 fa fa-envelope' />
          </div>
          {/* <div>Email:</div> */}
          <div className={`ml-2 ${css(styles.dataValue)}`}>
            {email && email !== ''
              ? <a href={`mailto:${email}`}>{email}</a>
              : <div style={{ color: salmon[500] }}>Required</div>
            }
          </div>
        </div>
        {/* Home Address */}
        <div className='fx mt-2'>
          <div style={{ width: '20px' }}>
            <i className='mr-2 fa-lg fa fa-home' />
          </div>
          {/* <div>Home Address:</div> */}
          <div className={`ml-2 ${css(styles.dataValue)}`}>
            {homeAddressStreet !== '' && homeAddressCity !== '' && homeAddressState !== '' && homeAddressZip !== ''
              ? <a
                href={homeAddressUrl}
                target='_blank'
                rel="noreferrer"
              >
                <div>
                  {`${homeAddressStreet}${homeAddressSuite ? ` Suite # ${homeAddressSuite}` : ''}`}
                </div>
                <div>
                  {`${homeAddressCity}${homeAddressState.length > 0 ? `, ${homeAddressState}` : ''} ${homeAddressZip}`}
                </div>
              </a>
              : <div style={{ color: salmon[500] }}>Required</div>
            }
          </div>
        </div>
        {/* Mailing Address */}
        <div className='fx fx__v-center mt-2'>
          <svg
            width="20"
            height="14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.578 13.546H1.422A1.42 1.42 0 0 1 0 12.126V5.306C0 2.375 2.38 0 5.317 0h9.366A5.311 5.311 0 0 1 20 5.305v6.822a1.42 1.42 0 0 1-1.422 1.419Z"
              fill="#5079AB"
            />
            <path
              d="M5.445 2.053H5.14a3.046 3.046 0 0 0-3.05 3.042v6.475h6.404V5.095a3.046 3.046 0 0 0-3.05-3.042Zm1.971 3.474c0 .447-.363.81-.812.81h-2.62a.811.811 0 1 1 0-1.62h2.62c.449 0 .812.362.812.81Z"
              fill={gray[700]}
            />
            <path
              d="M17.968 4.957c-.085-2.014-2.015-2.854-3.07-2.895-1.52-.059-6.085.014-6.085.014.996 1.063 1.191 2.218 1.22 3.035.058 1.618.014 6.476.014 6.476h7.934s.098-3.96-.014-6.63Zm-1.573.77a.55.55 0 0 1-.006.084v1.545a.566.566 0 0 1-.567.566h-.979a.566.566 0 0 1-.567-.566V6.292H12.41a.75.75 0 0 1-.75-.75v-.064a.75.75 0 0 1 .75-.75h3.42c.313 0 .566.254.566.566v.433Z"
              fill={gray[700]}
            />
          </svg>
          <div className={`ml-2 ${css(styles.dataValue)}`}>
            {mailingAddressSameAsHomeAddress
              ? <div>Same As Home Address</div>
              : <div>
                {mailingAddressStreet !== '' && mailingAddressCity !== '' && mailingAddressState && mailingAddressZip !== ''
                  ? <a
                    href={mailingAddressUrl}
                    target='_blank'
                    rel="noreferrer"
                  >
                    <div>
                      {`${mailingAddressStreet}${mailingAddressSuite && mailingAddressSuite ? ` Suite # ${mailingAddressSuite}` : ''}`}
                    </div>
                    <div>
                      {`${mailingAddressCity}${mailingAddressState.length > 0 ? `, ${mailingAddressState}` : ''} ${mailingAddressZip}`}
                    </div>
                  </a>
                  : <div style={{ color: salmon[500] }}>Required</div>
                }
              </div>
            }
          </div>
        </div>
      </div>

      {/* Patient ID - Front */}
      <div className='mt-4 mb-1'>
        <div className='fx fx__v-center'>
          {idIcon}
          <div className='ml-2'>
            Patient ID (Front)
          </div>
        </div>
        <img
          className='mt-2'
          src={patientIdFrontScan}
          style={{
            width: '343px',
            maxWidth: '343px',
            height: 'auto'
          }}
        />
      </div>

      {/* Patient ID - Back */}
      <div className='mt-4 mb-1'>
        <div className='fx fx__v-center'>
          {idIcon}
          <div className='ml-2'>
            Patient ID (Back)
          </div>
        </div>
        <img
          className='mt-2'
          src={patientIdBackScan}
          style={{
            width: '343px',
            maxWidth: '343px',
            height: 'auto'
          }}
        />
      </div>
    </div>
  );
};

PatientInfo.defaultProps = {
  className: 'p-4',
  noEdit: false
};

export default PatientInfo;
